import React, { useState, useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Popover from "@mui/material/Popover";
import { makeStyles } from "@material-ui/core/styles";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import moment from "moment";
import { publicService } from "_services";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import CircularProgress from "@mui/material/CircularProgress";
import { TextField } from "@material-ui/core";

var formatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  // These options are needed to round to whole numbers if that's what you want.
  //minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
  //maximumFractionDigits: 0, // (causes 2500.99 to be printed as $2,501)
});

const useStyles = makeStyles((theme) => ({
  form: {
    width: "100%", // Fix IE 11 issue.
    marginTop: "-100px",
    padding: "40px",
  },
  formHidden: {
    display: "none",
  },
  button: {
    fontFamily: "Roboto",
    fontWeight: "500",
    fontSize: "0.875rem",
    lineHeight: "1.75",
    letterSpacing: "0.02857em",
    textTransform: "uppercase",
    color: "rgba(0, 0, 0, 0.87)",
    backgroundColor: "#8bc34a",
    minWidth: "64px",
    width: "100%",
    padding: "6px 16px",
    borderRadius: "4px",
    border: "none",
    cursor: "pointer",
    boxShadow: "0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)",
  },
}));

export default function MakePaymentForm({ lobid, isRestricted, isEmpty }) {
  const classes = useStyles();
  const history = useHistory();

  const [anchorNeedHelp, setAnchorNeedHelp] = useState();
  const [open, setOpen] = useState(null);
  const handleClick = (event, id) => {
    setAnchorNeedHelp(event.currentTarget);
    setOpen(id);
  };

  const handleClose = () => {
    setAnchorNeedHelp(null);
    setOpen(null);
  };

  // const open = Boolean(anchorNeedHelp);
  const id = open ? "simple-popover" : undefined;

  const payment = useSelector((state) => state?.payment?.info);
  const paymentLoading = useSelector((state) => state?.payment?.loading);
  const {
    claimID,
    ccconfcode,
    RespName2,
    OutDate,
    LocAddress,
    LocCity,
    LocState,
    LocZip,
    addressUpdate,
    updatedAddress,
    updatedCity,
    updatedState,
    updatedZip,
    CreditCardPayAmount,
    RAEmail,
    JointEntityID,
    Entity,
    SuperRegionID,
    status,
    settlementamount,
  } = payment || {};

  const [paid, setPaid] = useState(false);
  const [paymentLogStatus, setPaymentLogStatus] = useState("idle");
  const paymentHandled = useRef(false);
  const mounted = useRef(false);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };

    const unblock = history.block(
      "You are leaving the Payment Center. If you have started a payment, please stay until your " +
        "payment confirmation is displayed. Are you sure you want to leave?"
    );
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      unblock();
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [history]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const [scriptLoaded, setScriptLoaded] = useState(false);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  useEffect(() => {
    let active = true;
    let loadTimeout;
    setScriptLoaded(false);

    const handleComplete = async (data) => {
      // Paystand can emit the same completion more than once.
      if (!active || paymentHandled.current) return;
      paymentHandled.current = true;

      try {
        const payload = {
          entity: Number(lobid) === 2 && SuperRegionID === 4 ? "Piedmont Gas" : Entity,
          email: RAEmail,
          claimID: claimID,
          receipt: data.response.data,
          LOBID: lobid,
        };

        const logRequest = publicService.logPayment(payload);
        setPaid(true);
        setPaymentLogStatus("saving");

        publicService.emailRecoveryAgent(payload);

        await logRequest;
        if (mounted.current) setPaymentLogStatus("saved");
      } catch (error) {
        if (mounted.current) {
          setPaid(true);
          setPaymentLogStatus("error");
        }
      }
    };

    const script = document.createElement("script");
    script.src = `https://checkout.paystand.${
      process.env.REACT_APP_ENV === "prod" ? "com" : "co"
    }/v4/js/paystand.checkout.js`;
    script.async = true;
    script.id = "ps_checkout";
    script.setAttribute("ps-env", process.env.REACT_APP_ENV === "prod" ? "live" : "sandbox");
    script.setAttribute("ps-publishableKey", process.env.REACT_APP_PAYSTAND_PUBLISHABLE_KEY);
    script.setAttribute("ps-mode", "modal");
    script.setAttribute("ps-checkoutType", "checkout_payment");
    if (isDesktop) script.setAttribute("ps-width", "1000px");
    script.setAttribute("ps-viewFunds", "bank, ach, card");
    script.setAttribute("ps-fixedAmount", "true");
    script.setAttribute(
      "ps-logoUrl",
      `https://apptest.prgconsulting.net/static/media/PRG-Logo.80ebd6a6b2689064a6880a5120c9a59c.svg`
    );
    script.setAttribute("ps-logoBgColor", "#fff");
    script.setAttribute("ps-headerColor", "#8bc34a");
    script.setAttribute("ps-externalCss", `${window.location.origin}/paystand.css`);

    const handleLoad = () => {
      if (!active) return;
      const checkout = window.psCheckout;
      if (!checkout) return;
      checkout.onComplete(handleComplete);
      loadTimeout = setTimeout(() => {
        if (active) setScriptLoaded(true);
      }, 2000);
    };

    script.addEventListener("load", handleLoad);

    document.body.appendChild(script);

    return () => {
      active = false;
      clearTimeout(loadTimeout);
      script.removeEventListener("load", handleLoad);
      document.body.removeChild(script);
    };
  }, [payment, claimID, ccconfcode, CreditCardPayAmount, Entity, RAEmail, SuperRegionID, lobid, isDesktop]);

  const [forbidden, setForbidden] = useState(false);
  const [paidInFull, setPaidInFull] = useState(false);
  const [noBalanceDue, setNoBalanceDue] = useState(false);

  useEffect(() => {
    setForbidden(
      [
        "F2__Coll/Legal",
        "F6A_Forwarded to Legal_Underground",
        "F6B_Forwarded to Legal_UIM",
        "F6C_Forwarded to Client Internal",
        "F6D_Fowarded to Legal_Unidentified",
        "F2A__Optimum Legal",
        "F4_Forwarded to Charter Internal",
        "F5__RO Forwarded to Charter Internal",
        "F6A_Forward to Legal_Underground",
        "F6B_Forward to Legal_UIM",
        "F7_Outside Collection Agency",
      ].includes(status)
    );

    setPaidInFull(status === "E2__Payment Received");
  }, [status]);

  useEffect(() => {
    setNoBalanceDue(!CreditCardPayAmount || parseFloat(CreditCardPayAmount) <= 0);
  }, [CreditCardPayAmount]);

  const [error, setError] = useState(false);
  const [email, setEmail] = useState(null);
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (email && !email?.match(/^[a-zA-Z0–9._%+-]+@[a-zA-Z0–9.-]+\.[a-zA-Z]{2,}$/)) {
      setError(true);
    } else {
      setError(false);
    }
  }, [email]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      email: email,
      notes: notes.replace(/</g, "").replace(/>/g, ""),
      RAEmail: RAEmail,
      claimID: claimID,
      status: status,
      resp: RespName2,
    };
    publicService.contactRAForAssistance(payload);
    e.target.reset();
  };

  return (
    <Box className={!isRestricted && !isEmpty ? classes.form : classes.formHidden}>
      <Box sx={{ textAlign: "center" }}>
        <Typography variant="h5">Outside Plant Damage (OPD) Recovery Services</Typography>
        <Typography variant="h5">PAYMENT CENTER</Typography>
      </Box>

      <Divider sx={{ margin: "20px 0 30px" }} />

      {payment && !paid && !paidInFull && !forbidden ? (
        <Grid container spacing={{ xs: 4, md: 12 }}>
          <Grid item xs={12} md={6}>
            <Stack spacing={2}>
              <Typography variant="button">Claim Details</Typography>
              <Grid container>
                <Grid item xs={6}>
                  <Typography variant="body2">Claim Invoice No.</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">{claimID}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">Responsible Party</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">{RespName2}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">Date of Damage</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">{moment(OutDate).format("MM/DD/YYYY")}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">Location of Damage</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">{addressUpdate ? updatedAddress : LocAddress}</Typography>
                  <Typography variant="body2">
                    {addressUpdate ? updatedCity : LocCity}, {addressUpdate ? updatedState : LocState}{" "}
                    {addressUpdate ? updatedZip : LocZip}
                  </Typography>
                </Grid>
              </Grid>
              <Grid container>
                <Grid item xs={6}>
                  <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                    Amount to be paid
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                    {formatter.format(CreditCardPayAmount)}
                  </Typography>
                </Grid>
              </Grid>

              <Button
                aria-describedby={id}
                variant="text"
                onClick={(event) => handleClick(event, "need-help")}
                sx={{ width: "fit-content" }}
              >
                Need help?
              </Button>
              <Popover
                id={id}
                open={open === "need-help"}
                anchorNeedHelp={anchorNeedHelp}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
              >
                <Box sx={{ p: 4 }}>
                  <Stack spacing={2}>
                    <Typography variant="button">HELP WITH YOUR OPD PAYMENT</Typography>
                    <Typography variant="body1">
                      Your PRG Recovery Agent can assist you with making your claim payment.
                    </Typography>
                    <Typography variant="body2">
                      <a href="mailto:jlosch@PRGconsulting.net">Jay Losch</a> - (720) 795-9960
                    </Typography>
                  </Stack>
                </Box>
              </Popover>
            </Stack>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper elevation={8} sx={{ textAlign: "center", padding: "30px", borderRadius: "4px" }}>
              <Typography variant="button" sx={{ marginBottom: "20px", paddingBottom: "20px" }}>
                OPD CLAIM PAYMENT{process.env.REACT_APP_ENV !== "prod" && " (PAYSTAND TESTING)"}
              </Typography>
              {!paymentLoading && !paid ? (
                <Button
                  variant="contained"
                  className={`${classes.button} ps-button`}
                  // ps-checkoutType="checkout_payment"
                  ps-paymentAmount={CreditCardPayAmount?.toString()}
                  ps-cf_claim_id={claimID}
                  ps-cf_confirmation_code={ccconfcode}
                  ps-accountKey={
                    JointEntityID === 4
                      ? process.env.REACT_APP_PAYSTAND_ACCOUNT_KEY_COMCAST
                      : process.env.REACT_APP_PAYSTAND_ACCOUNT_KEY_DEFAULT
                  }
                  ps-checkoutType={
                    CreditCardPayAmount > 1428.57
                      ? process.env.REACT_APP_PAYSTAND_PRESET_KEY_OVER_5
                      : process.env.REACT_APP_PAYSTAND_PRESET_KEY_UNDER_5
                  }
                  disabled={!scriptLoaded}
                >
                  {scriptLoaded ? (
                    `Pay ${formatter.format(CreditCardPayAmount)}`
                  ) : (
                    <CircularProgress color="primary" size={25} />
                  )}
                </Button>
              ) : (
                <Typography sx={{ color: "red", fontSize: "12px", marginTop: "10px" }}>
                  {!scriptLoaded
                    ? "There was a problem loading the checkout interface. Please contact support."
                    : "No amount due at this time"}
                </Typography>
              )}
              <Box sx={{ marginTop: "20px" }}>
                <Typography variant="caption">
                  Payment can be made with no additional fee by logging directly into your bank account. For your
                  convenience, other payment methods are available, though a convenience fee may apply. Any convenience
                  fees will be detailed before you are charged
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      ) : paid ? (
        <Box sx={{ textAlign: "center", mt: "80px", mb: "80px" }} aria-live="polite">
          <Typography variant="h6">Your payment has been processed.</Typography>
          {paymentLogStatus === "saved" ? (
            <Typography>You may exit from this page when you are finished.</Typography>
          ) : paymentLogStatus === "error" ? (
            <Typography color="error">
              We could not confirm that your payment was recorded. Please do not submit another payment.
              Contact your Recovery Agent with your payment confirmation and claim invoice number {claimID}.
            </Typography>
          ) : (
            <Typography>Please keep this page open while we confirm your payment record.</Typography>
          )}
        </Box>
      ) : paidInFull ? (
        <Typography variant="h5" textAlign="center" mt="80px" mb="80px">
          This claim has already been settled.
        </Typography>
      ) : noBalanceDue ? (
        <Typography variant="h5" textAlign="center" mt="80px" mb="80px">
          No Payment is due at this time.
        </Typography>
      ) : forbidden ? (
        <>
          <Typography variant="h5" textAlign="center" mt="80px" mb="80px">
            This claim requires additional assistance. Please use the form below to contact the Recovery Agent
          </Typography>
          <form onSubmit={handleSubmit}>
            <TextField
              name="email"
              label="Your Email Address"
              variant="outlined"
              style={{ marginLeft: "30%", marginRight: "30%", width: "40%" }}
              onChange={(e) => setEmail(e.target.value)}
              error={error}
              helperText={error ? "Invalid Email Address" : ""}
              required={true}
            />
            <TextField
              name="notes"
              multiline
              minRows={3}
              label="Additional Notes"
              placeholder="Optional..."
              variant="outlined"
              style={{ marginLeft: "30%", marginRight: "30%", width: "40%", marginTop: "1rem" }}
              onChange={(e) => setNotes(e.target.value)}
              maxRows={5}
            />
            <Button
              type="submit"
              color="primary"
              variant="contained"
              style={{ marginLeft: "60%", marginRight: "30%", width: "10%", marginTop: "1rem" }}
            >
              Submit
            </Button>
          </form>
        </>
      ) : (
        <Typography sx={{ textAlign: "center", marginBottom: "40px", color: "red" }}>No claim found</Typography>
      )}
      <Stack sx={{ textAlign: "center", alignItems: "center", mt: "20px" }}>
        <Typography variant="caption">
          This website is secured with SSL Encryption. The safe processing of your claim is our top priority.
        </Typography>
        <Button
          variant="text"
          onClick={(event) => handleClick(event, "privacy-statement")}
          sx={{ width: "fit-content" }}
        >
          Privacy Statement
        </Button>
        <Popover
          id={id}
          open={open === "privacy-statement"}
          anchorNeedHelp={anchorNeedHelp}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
        >
          <Box sx={{ p: 4 }}>
            <Stack spacing={2}>
              <Typography variant="button">PRIVACY STATEMENT</Typography>
              <Typography variant="body2">
                This privacy policy discloses the privacy practices for https://app.prgconsulting.net. This privacy
                policy applies solely to information collected by this web site.
              </Typography>
              <Typography variant="button">Security</Typography>
              <Typography variant="body2">
                PRG takes precautions to protect your information. When you submit sensitive information via the
                website, your information is protected both online and offline. Wherever you are asked for sensitive
                information (such as credit card data), that information is encrypted and transmitted to a PCI Compliant
                third-party processor in a secure way. You can verify this by looking for a closed lock icon in the
                address section of the website, or looking for "https" at the beginning of the address of the web page.
                Each page within this site also links to the Secure Certificate which users can validate.
              </Typography>
              <Typography variant="body2">
                While PRG uses encryption to protect sensitive information transmitted online, we also protect your
                information offline. PRG never collects or stores your credit card number, expiration date or type of
                card used. PRG does collect the transaction authorization number, which will allow us to reference the
                transaction with the third-party processor should a need arise.
              </Typography>
            </Stack>
          </Box>
        </Popover>
        <Typography variant="caption">©Project Resources Group, 2024.</Typography>
      </Stack>
    </Box>
  );
}
